import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Activity,
  Bot,
  CalendarDays,
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  FileText,
  ShieldCheck,
  Sparkles,
  Stethoscope,
  Users,
  Zap,
} from "lucide-react";

const slides = [
  {
    eyebrow: "AI Scribe Live",
    title: "Visit becomes a ready chart.",
    accent: "#9FD0EC",
    icon: Stethoscope,
    center: "SOAP",
    desc: "Astra captures visit context, drafts the note and prepares orders before the patient leaves.",
    chips: [
      { icon: FileText, label: "Chief complaint", value: "Captured" },
      { icon: Bot, label: "AI note", value: "Drafted" },
      { icon: ClipboardCheck, label: "Orders", value: "Queued" },
      { icon: CheckCircle2, label: "Coding hints", value: "Ready" },
    ],
    outcomes: ["Notes complete", "Charge ready", "Patient focused"],
  },
  {
    eyebrow: "Revenue Guardrails",
    title: "Claims clear before submit.",
    accent: "#76D7FF",
    icon: ShieldCheck,
    center: "RCM",
    desc: "Astra checks eligibility, authorization, claim edits and denial risk before submission.",
    chips: [
      { icon: ShieldCheck, label: "Eligibility", value: "Verified" },
      { icon: ClipboardCheck, label: "Authorization", value: "Matched" },
      { icon: CircleDollarSign, label: "Claim edits", value: "Cleared" },
      { icon: Activity, label: "Denial risk", value: "Low" },
    ],
    outcomes: ["Fewer reworks", "Faster pay", "AR prioritized"],
  },
  {
    eyebrow: "Practice Command",
    title: "Teams work from one view.",
    accent: "#BFEAFF",
    icon: Zap,
    center: "AIOS",
    desc: "Astra keeps scheduling, front desk, providers and next-best actions moving from one view.",
    chips: [
      { icon: CalendarDays, label: "Schedule", value: "Balanced" },
      { icon: Users, label: "Front desk", value: "In sync" },
      { icon: Activity, label: "Provider flow", value: "Live" },
      { icon: Sparkles, label: "Next action", value: "Suggested" },
    ],
    outcomes: ["One login", "Zero silos", "Demo ready"],
  },
];

export function ClinicalCommandVisual({ compact = false, showBrand = true, bare = false }) {
  const [active, setActive] = useState(0);
  const slide = slides[active];
  const CenterIcon = slide.icon;

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((current) => (current + 1) % slides.length);
    }, 8000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <div className={`relative overflow-hidden ${compact ? "h-full min-h-0" : "min-h-[430px] md:min-h-[500px]"} ${bare ? "bg-transparent" : "bg-[#071F33]"}`}>
      {!bare && (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_18%,rgba(159,208,236,0.34),transparent_27%),radial-gradient(circle_at_76%_22%,rgba(40,128,184,0.34),transparent_28%),radial-gradient(circle_at_52%_86%,rgba(118,215,255,0.22),transparent_28%),linear-gradient(135deg,#061A2B_0%,#0C2A40_48%,#155C86_100%)]" />
      )}
      <div className="absolute inset-0 opacity-20 dot-grid" />
      <motion.div
        animate={{ opacity: [0.2, 0.42, 0.2], scale: [1, 1.06, 1] }}
        transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut" }}
        className="absolute left-1/2 top-1/2 h-72 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#9FD0EC]/20 blur-3xl"
      />

      <div className={`relative z-10 h-full ${compact ? "p-4 xl:p-5" : "p-6 md:p-8"} flex flex-col`}>
        {showBrand && (
          <div className="flex items-start gap-4">
            <div className="rounded-2xl bg-white/10 border border-white/15 p-3 backdrop-blur">
              <img src="/astra-logo.png" alt="Astra" className={`${compact ? "h-10" : "h-14"} w-auto`} />
            </div>
          </div>
        )}

        <div className={`flex-1 min-h-0 grid ${compact ? "grid-cols-1 gap-4 py-3" : "grid-cols-1 xl:grid-cols-[1.05fr_0.95fr] gap-6 py-6"} items-center`}>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
              >
                <div className={compact ? "h-[86px] xl:h-[98px]" : "h-[204px] overflow-hidden flex flex-col justify-start"}>
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#9FD0EC]">{slide.eyebrow}</p>
                  <h3 className={`${compact ? "mt-2 text-2xl xl:text-3xl max-w-[360px]" : "mt-4 h-[124px] text-[40px] max-w-[330px]"} font-display font-bold tracking-tight leading-[1.02] text-white`}>
                    {slide.title}
                  </h3>
                  {!compact && (
                    <p className="max-w-[310px] text-sm leading-relaxed text-[#A9C4DD]">
                      {slide.desc}
                    </p>
                  )}
                </div>
              </motion.div>
            </AnimatePresence>

            {compact ? (
              <div className="mt-5 space-y-2.5">
                {slide.chips.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={`${slide.center}-${item.label}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.07, duration: 0.4 }}
                      className="flex h-12 items-center gap-3 rounded-full border border-white/12 bg-white/[0.07] px-4 backdrop-blur"
                    >
                      <Icon size={16} className="text-[#9FD0EC] shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-mono text-[9px] tracking-[0.16em] uppercase text-[#9FD0EC] truncate">{item.label}</div>
                        <div className="text-sm font-semibold leading-tight text-white truncate">{item.value}</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-5 grid grid-cols-2 gap-3">
                {slide.chips.map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={`${slide.center}-${item.label}`}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.07, duration: 0.4 }}
                      className="h-[88px] rounded-xl border border-white/15 bg-white/10 p-3 backdrop-blur"
                    >
                      <Icon size={17} className="text-[#9FD0EC]" />
                      <div className="mt-2 font-mono text-[9px] tracking-[0.16em] uppercase text-[#9FD0EC] truncate">{item.label}</div>
                      <div className="mt-1 text-sm font-semibold leading-tight text-white truncate">{item.value}</div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="relative flex items-center justify-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 32, ease: "linear" }}
              className={`${compact ? "h-28 w-28 xl:h-36 xl:w-36" : "h-52 w-52 md:h-60 md:w-60"} relative rounded-full border border-[#9FD0EC]/25`}
            >
              <div className="absolute inset-5 rounded-full border border-dashed border-[#9FD0EC]/35" />
              <div className="absolute inset-12 rounded-full border border-[#9FD0EC]/20" />
              {[0, 1, 2, 3].map((dot) => (
                <span
                  key={dot}
                  className="absolute h-3 w-3 rounded-full bg-[#9FD0EC] shadow-[0_0_20px_rgba(159,208,236,0.9)]"
                  style={{
                    left: `${50 + Math.cos((dot * Math.PI) / 2) * 48}%`,
                    top: `${50 + Math.sin((dot * Math.PI) / 2) * 48}%`,
                  }}
                />
              ))}
            </motion.div>

            <AnimatePresence mode="wait">
              <motion.div
                key={slide.center}
                initial={{ opacity: 0, scale: 0.82 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.88 }}
                transition={{ duration: 0.45 }}
                className={`${compact ? "h-[72px] w-[72px] xl:h-[88px] xl:w-[88px]" : "h-32 w-32"} absolute rounded-full bg-white shadow-[0_0_80px_rgba(159,208,236,0.48)] flex flex-col items-center justify-center`}
              >
                <CenterIcon size={compact ? 30 : 38} className="text-[#2880B8]" strokeWidth={1.4} />
                <span className="mt-2 font-mono text-[11px] tracking-[0.18em] font-bold uppercase text-[#0C2A40]">{slide.center}</span>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {!compact && (
          <div className="grid grid-cols-3 gap-3">
            {slide.outcomes.map((label, i) => (
              <motion.div
                key={`${slide.center}-${label}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
                className="h-16 rounded-xl border border-white/15 bg-white/10 px-4 py-3 flex items-center"
              >
                <div className="flex items-center gap-2 text-sm font-semibold leading-tight text-white">
                  <CheckCircle2 size={16} className="text-[#9FD0EC] shrink-0" />
                  {label}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-5 flex items-center">
          <div className="flex gap-2">
            {slides.map((item, i) => (
              <button
                key={item.center}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Show ${item.eyebrow}`}
                className={`h-2 rounded-full transition-all duration-300 ${i === active ? "w-8 bg-[#9FD0EC]" : "w-2 bg-white/25 hover:bg-white/45"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
